/**
 * ADR-0243 — how a person is named in the visits panel.
 *
 *   name      "Visitor from Austin"          (city → region → country)
 *   device    "Chrome on Mac"                (browser + OS, no version)
 *   one line  "Visitor from Austin · Chrome on Mac"
 *
 * Never an IP, never an id: the label is built only from the coarse
 * location and the closed device labels the server returns.
 */

import { humanDeviceLabel, humanVisitorName, visitorCountryName, visitorPlaceName } from '@sill/audit';
import type { PersonDevice, PersonLocation, PersonSummary } from '@sill/types/api';

/**
 * "Austin" → else region → else country name; null when nothing known.
 * The SAME rule names a single human record (`humanRecordName` in
 * `@sill/audit`), so a person row and a record row read identically.
 */
export function placeLabel(loc: PersonLocation | null): string | null {
  return visitorPlaceName(loc);
}

/** "Austin, Texas, United States" — the full line for the detail header. */
export function fullPlaceLabel(loc: PersonLocation | null): string | null {
  if (loc === null) return null;
  const parts = [loc.city, loc.region, visitorCountryName(loc.country)].filter((p): p is string => typeof p === 'string' && p.length > 0);
  const unique = parts.filter((p, i) => parts.indexOf(p) === i);
  return unique.length > 0 ? unique.join(', ') : null;
}

/** "Chrome on Mac" / "Chrome" / "iPhone" / null. */
export function deviceLabel(d: PersonDevice): string | null {
  return humanDeviceLabel(d);
}

/** The overflow bucket is many strangers, not a person (security review L1). */
const OVERFLOW_LABEL = 'Other new visitors (over the hourly limit)';

/** Line 1: "Visitor from Austin" / "Visitor". */
export function personName(p: Pick<PersonSummary, 'location'> & { readonly key_kind?: PersonSummary['key_kind'] }): string {
  if (p.key_kind === 'overflow') return OVERFLOW_LABEL;
  return humanVisitorName(p.location);
}

/** One-line form: "Visitor from Austin · Chrome on Mac". */
export function personLabel(p: Pick<PersonSummary, 'location' | 'device'> & { readonly key_kind?: PersonSummary['key_kind'] }): string {
  if (p.key_kind === 'overflow') return OVERFLOW_LABEL;
  const head = personName(p);
  const dev = deviceLabel(p.device);
  return dev !== null ? `${head} · ${dev}` : head;
}

/** 0 → "under 1 min", 75 → "1 min", 3720 → "1 h 2 min". */
export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 60) return 'under 1 min';
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest === 0 ? `${h} h` : `${h} h ${rest} min`;
}
